import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FiColumns, FiDownload, FiExternalLink, FiFileText, FiMessageSquare, FiUploadCloud, FiUser } from "react-icons/fi";
import styles from "./CampaignAudience.module.scss";
import locale from "../../../constants/locale";
import routes from "../../../constants/routes";
import { useStore } from "../../../stores";
import {
  CampaignFileConfiguration,
  CampaignFileDraft,
  CampaignFileIssue,
  CampaignFilePreview,
  CampaignFileVariableMapping,
  ChatChannel,
} from "../../../models";
import { postCampaignFilePreview } from "../../../services/chathub";

const initialConfiguration = (): CampaignFileConfiguration => ({
  version: 2,
  sheetIndex: 0,
  headerRowNumber: null,
  recipientColumnId: null,
  firstNameColumnId: null,
  lastNameColumnId: null,
  invalidRowPolicy: "block",
  duplicatePolicy: "keep_all",
});

const phoneChannels = new Set([ChatChannel.MetaWhatsApp, ChatChannel.TwilioWhatsApp, ChatChannel.Wazzup]);

const sourceForToken = (token: string, columns: CampaignFilePreview["columns"]) => {
  if (/^\d+$/.test(token)) return "";
  const normalized = (value: string) => value.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, "");
  const matches = columns.filter((column) => normalized(column.header) === normalized(token));
  return matches.length === 1 ? matches[0].id : "";
};

const blankMappings = (message: string): CampaignFileVariableMapping[] => {
  const tokens = Array.from(message.matchAll(/{{\s*([^{}]+?)\s*}}/g))
    .map((match) => match[1].trim())
    .filter((token, index, all) => token && all.indexOf(token) === index);
  return tokens.map((token) => ({
    source: `{{${token}}}`,
    kind: "file_column",
    value: "",
    fallback: null,
  }));
};

type Props = {
  file?: File;
  setFile: (file?: File) => void;
  channel: ChatChannel;
  template: any;
  message: string;
  draft: CampaignFileDraft;
  setDraft: (draft: CampaignFileDraft) => void;
  bot: { id: number; key: string; password: string };
  contactFields: string[];
  serverError?: string;
};

export default function CampaignFileUpload({
  file,
  setFile,
  channel,
  template,
  message,
  draft,
  setDraft,
  bot,
  contactFields,
  serverError,
}: Props) {
  const { t } = useTranslation();
  const { routeTo } = useStore("bots");
  const issueMessage = (issue: CampaignFileIssue) => {
    if (issue.identityConflictType === "digits_only") return t(locale.campaignFileIdentityDigits);
    if (issue.identityConflictType === "multiple_records") return t(locale.campaignFileIdentityMultiple);
    return issue.message;
  };
  const [pending, setPending] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [sampleOffset, setSampleOffset] = useState(0);
  const [loadingMoreIssues, setLoadingMoreIssues] = useState(false);
  const requestGeneration = useRef(0);
  const invalidatePreviewRequests = useCallback(() => {
    requestGeneration.current++;
  }, []);
  const configuration = draft?.configuration || initialConfiguration();
  const fieldsMapping = draft?.fieldsMapping || blankMappings(message);
  const preview = draft?.preview || null;
  const currentState = useRef({ draft, configuration, fieldsMapping, preview });
  currentState.current = { draft, configuration, fieldsMapping, preview };

  const requestKey = useMemo(() => JSON.stringify({
    channel,
    botId: bot.id,
    name: file?.name,
    size: file?.size,
    lastModified: file?.lastModified,
    template,
    message,
    configuration,
    fieldsMapping,
  }), [channel, bot.id, file, template, message, configuration, fieldsMapping]);

  const sendPreview = useCallback(async (
    offset = 0,
    issueOffset = 0,
    appendIssues = false,
  ) => {
    if (!file) return;
    const current = currentState.current;
    const generation = ++requestGeneration.current;
    setPending(true);
    setPreviewError("");
    try {
      const response = await postCampaignFilePreview(
        bot.id,
        bot.key,
        bot.password,
        channel,
        file,
        JSON.stringify({
          message,
          template: template || null,
          fieldsMapping: current.fieldsMapping,
          audienceFile: current.configuration,
        }),
        offset,
        issueOffset,
      ) as CampaignFilePreview;
      if (generation !== requestGeneration.current) return;

      let nextConfiguration = current.configuration;
      let nextMappings = current.fieldsMapping;
      if (response.selectedSheetIndex === current.configuration.sheetIndex && !current.configuration.headerRowNumber) {
        nextConfiguration = { ...nextConfiguration, headerRowNumber: response.headerRowNumber };
      }
      let recipientSuggestionResolved = current.draft.recipientSuggestionResolved || false;
      if (!recipientSuggestionResolved) {
        if (!nextConfiguration.recipientColumnId) {
          nextConfiguration = { ...nextConfiguration, recipientColumnId: response.recipientSuggestion };
        }
        recipientSuggestionResolved = true;
      }
      const nameSuggestionsResolved = { ...(current.draft.nameSuggestionsResolved || { first: false, last: false }) };
      if (!nameSuggestionsResolved.first) {
        if (!nextConfiguration.firstNameColumnId) {
          nextConfiguration = { ...nextConfiguration, firstNameColumnId: response.firstNameSuggestion };
        }
        nameSuggestionsResolved.first = true;
      }
      if (!nameSuggestionsResolved.last) {
        if (!nextConfiguration.lastNameColumnId) {
          nextConfiguration = { ...nextConfiguration, lastNameColumnId: response.lastNameSuggestion };
        }
        nameSuggestionsResolved.last = true;
      }
      if (current.preview === null || response.selectedSheetIndex !== current.preview.selectedSheetIndex || response.headerRowNumber !== current.preview.headerRowNumber) {
        nextMappings = current.fieldsMapping.map((mapping) => {
          if (mapping.kind !== "file_column" || mapping.value) return mapping;
          const token = mapping.source.replace(/[{}]/g, "").trim();
          return { ...mapping, value: sourceForToken(token, response.columns) };
        });
      }

      const configChanged = JSON.stringify(nextConfiguration) !== JSON.stringify(current.configuration);
      const mappingsChanged = JSON.stringify(nextMappings) !== JSON.stringify(current.fieldsMapping);
      const acceptedPreview = appendIssues && current.preview?.revision === response.revision
        ? { ...response, issues: [...current.preview.issues, ...response.issues] }
        : response;
      setDraft({
        ...current.draft,
        botId: bot.id,
        channel,
        file,
        configuration: nextConfiguration,
        fieldsMapping: nextMappings,
        preview: acceptedPreview,
        previewStale: configChanged || mappingsChanged,
        recipientSuggestionResolved,
        nameSuggestionsResolved,
      });
      if (!appendIssues) setSampleOffset(offset);
    } catch (error: any) {
      if (generation !== requestGeneration.current) return;
      setPreviewError(error?.message || t(locale.campaignFilePreviewError));
      setDraft({
        ...currentState.current.draft,
        previewStale: Boolean(currentState.current.preview),
      });
    } finally {
      if (generation === requestGeneration.current) {
        setPending(false);
        setLoadingMoreIssues(false);
      }
    }
  }, [file, bot.id, bot.key, bot.password, channel, message, template, setDraft, t]);

  useEffect(() => {
    if (!file) return;
    const generation = ++requestGeneration.current;
    setPending(true);
    setPreviewError("");
    const timeout = window.setTimeout(() => {
      if (generation === requestGeneration.current) {
        void sendPreview(0, 0, false);
      }
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      // Invalidating unconditionally also covers requests started by sendPreview,
      // which advances this counter after the debounce effect begins.
      invalidatePreviewRequests();
    };
  }, [requestKey, file, sendPreview, invalidatePreviewRequests]);

  const updateConfiguration = (patch: Partial<CampaignFileConfiguration>, resetMappings = false) => {
    const next = {
      ...draft,
      configuration: { ...configuration, ...patch },
      fieldsMapping: resetMappings
        ? fieldsMapping.map((mapping) => mapping.kind === "file_column" ? { ...mapping, value: "" } : mapping)
        : fieldsMapping,
      preview,
      previewStale: Boolean(preview),
      ...(resetMappings ? {
        recipientSuggestionResolved: false,
        nameSuggestionsResolved: { first: false, last: false },
      } : {}),
      ...(!resetMappings && Object.prototype.hasOwnProperty.call(patch, "recipientColumnId") ? { recipientSuggestionResolved: true } : {}),
      ...(!resetMappings && Object.prototype.hasOwnProperty.call(patch, "firstNameColumnId") ? {
        nameSuggestionsResolved: { ...(draft.nameSuggestionsResolved || { first: false, last: false }), first: true },
      } : {}),
      ...(!resetMappings && Object.prototype.hasOwnProperty.call(patch, "lastNameColumnId") ? {
        nameSuggestionsResolved: { ...(draft.nameSuggestionsResolved || { first: false, last: false }), last: true },
      } : {}),
    };
    setDraft(next);
  };

  const updateMapping = (index: number, patch: Partial<CampaignFileVariableMapping>) => {
    const nextMappings = fieldsMapping.map((mapping, mappingIndex) =>
      mappingIndex === index ? { ...mapping, ...patch } : mapping
    );
    setDraft({ ...draft, fieldsMapping: nextMappings, preview, previewStale: Boolean(preview) });
  };

  const onFileChange = (nextFile?: File) => {
    // A response for the previous file must never repopulate its preview after
    // the user removes or replaces it.
    invalidatePreviewRequests();
    setPending(false);
    setFile(nextFile);
    setSampleOffset(0);
    setPreviewError("");
    if (!nextFile) {
      setDraft({
        ...draft,
        file: undefined,
        configuration: initialConfiguration(),
        fieldsMapping: blankMappings(message),
        preview: null,
        previewStale: false,
        recipientSuggestionResolved: false,
        nameSuggestionsResolved: { first: false, last: false },
      });
      return;
    }
    setDraft({
      botId: bot.id,
      channel,
      file: nextFile,
      configuration: initialConfiguration(),
      fieldsMapping: blankMappings(message),
      preview: null,
      previewStale: false,
      recipientSuggestionResolved: false,
      nameSuggestionsResolved: { first: false, last: false },
    });
  };

  const activeSample = preview?.sampleRows?.[0];
  const activeSampleIssues = activeSample?.issues || [];
  const mediaConfigured = template?.meta?.media || template?.mediaUrl || template?.meta?.header;
  const templateButtons = Array.isArray(template?.meta?.actions)
    ? template.meta.actions
    : Array.isArray(template?.meta?.buttons) ? template.meta.buttons : [];
  const sampleText = String(activeSample?.renderedText || message || template?.messageBody || "");
  const rowValue = (row: CampaignFilePreview["sampleRows"][number], columnId: string) => row.values[columnId] ?? "";
  const unresolvedTokens = new Set<string>();
  activeSampleIssues.forEach((issue) => {
    const match = issue.message.match(/template variable\s+{{\s*([^{}]+?)\s*}}/i);
    if (match) unresolvedTokens.add(match[1].trim());
  });
  const renderedSample = sampleText.split(/({{\s*[^{}]+?\s*}})/g).map((part, index) => {
    const match = part.match(/^{{\s*([^{}]+?)\s*}}$/);
    return match && unresolvedTokens.has(match[1].trim())
      ? <mark key={`${part}-${index}`} className="has-background-warning-light">{part}</mark>
      : part;
  });
  const isPhoneChannel = phoneChannels.has(channel);

  return (
    <div className={styles.upload}>
      <section className={styles.panel}>
        <div className={styles.sectionHeader}>
          <div>
            <h3 className={styles.sectionTitle}>{t(locale.campaignAudienceUpload)}</h3>
            <p className={styles.subtitle}>{t(locale.campaignFileHelp)}</p>
          </div>
          <a className={styles.sampleLink} href={`${process.env.PUBLIC_URL || ""}/campaigns/audience-sample.xlsx`} download>
            <FiDownload aria-hidden="true" /> {t(locale.campaignFileSampleDownload)}
          </a>
        </div>
        <div className={styles.dropzone}
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault();
            const droppedFile = event.dataTransfer.files[0];
            if (droppedFile) onFileChange(droppedFile);
          }}
        >
          {file ? <FiFileText className={styles.uploadIcon} aria-hidden="true" /> : <FiUploadCloud className={styles.uploadIcon} aria-hidden="true" />}
          <div className={styles.fileInfo}>
            <strong>{file ? file.name : t(locale.campaignAudienceDropFile)}</strong>
            <span>{file ? `${Math.max(1, Math.ceil(file.size / 1024))} KB · Excel` : t(locale.campaignAudienceFileHint)}</span>
          </div>
          <label className={`button is-primary ${file ? "is-outlined" : ""} ${styles.filePicker}`}>
            {t(file ? locale.campaignAudienceReplaceFile : locale.campaignAudienceChooseFile)}
            <input type="file" accept=".xlsx" value="" aria-label={t(file ? locale.campaignAudienceReplaceFile : locale.campaignAudienceChooseFile)} onChange={(event) => {
              const nextFile = event.target.files?.[0];
              if (nextFile) onFileChange(nextFile);
            }} />
          </label>
        </div>
        <div className={styles.sampleHelp}><p className="help">{t(locale.campaignHelpSample)}</p></div>
        {file && (
          <div className={styles.fileToolbar}>
            <div>
              {preview && preview.sheets.length > 1 && (
                <div className={styles.sheetPicker}>
                  <label className="label" htmlFor="campaign-sheet">{t(locale.campaignFileSheet)}</label>
                  <div className="select is-small is-fullwidth">
                    <select id="campaign-sheet" value={configuration.sheetIndex} onChange={(event) => updateConfiguration({
                      sheetIndex: Number(event.target.value), headerRowNumber: null,
                      recipientColumnId: null, firstNameColumnId: null, lastNameColumnId: null,
                    }, true)}>
                      {preview.sheets.map((sheet) => <option key={sheet.index} value={sheet.index}>{sheet.name}</option>)}
                    </select>
                  </div>
                  <p className="help">{t(locale.campaignHelpSheet)}</p>
                </div>
              )}
              {(pending || draft.previewStale) && <p role="status" className="help">{t(locale.campaignFileReading)}</p>}
            </div>
            <button type="button" className="button is-small is-white" onClick={() => onFileChange(undefined)}>{t(locale.campaignFileRemove)}</button>
          </div>
        )}
        {(previewError || serverError) && (
          <div role="alert" className={`${styles.notice} ${styles.noticeWarning} mt-3`}>
            {previewError || serverError}
            {file && <button type="button" className="button is-small ml-3" disabled={pending} onClick={() => void sendPreview()}>{t(locale.campaignFileRetry)}</button>}
          </div>
        )}
      </section>

      {preview && (
        <>
          <div className={styles.stats} aria-label={t(locale.campaignAudienceFileSummary)}>
            {[
              { label: locale.campaignAudienceContacts, value: preview.stats.dataRows },
              { label: locale.campaignFileColumns, value: preview.stats.columnCount },
              { label: locale.campaignFileDuplicateRows, value: preview.stats.duplicateRows ?? "—" },
              { label: locale.campaignFileMessagesReady, value: pending || draft.previewStale ? "—" : preview.stats.messagesReady, ready: true },
            ].map((stat) => (
              <div key={stat.label} className={`${styles.stat} ${stat.ready ? styles.statReady : ""}`}>
                <strong>{stat.value}</strong><span>{t(stat.label)}</span>
              </div>
            ))}
          </div>
          {preview.stats.dataRows === 0 && !pending && !draft.previewStale && (
            <div className={styles.notice} role="status">
              <strong>{t(locale.campaignAudienceEmptyTitle)}</strong>
              {t(locale.campaignAudienceEmptyHelp)}
            </div>
          )}
        </>
      )}

      <div className={styles.workspace}>
        <section className={`${styles.panel} ${styles.mappingPanel}`}>
          <h3 className={styles.sectionTitle}>{t(locale.campaignAudienceMatchColumns)}</h3>
          <p className={styles.subtitle}>{t(locale.campaignFileCampaignScope)}</p>
          {!preview ? (
            <div className={styles.emptyState}>
              <FiColumns aria-hidden="true" />
              <p>{t(locale.campaignAudienceMappingEmpty)}</p>
            </div>
          ) : (
            <>
              <div className={styles.recipient}>
                <label className="label" htmlFor="campaign-recipient-column">{t(isPhoneChannel ? locale.campaignFileRecipientColumn : locale.campaignFileRecipientIdColumn)}</label>
                <div className="select is-fullwidth">
                  <select id="campaign-recipient-column" value={configuration.recipientColumnId || ""} onChange={(event) => updateConfiguration({ recipientColumnId: event.target.value || null })}>
                    <option value="">{t(isPhoneChannel ? locale.campaignFileSelectRecipient : locale.campaignFileSelectRecipientId)}</option>
                    {preview.columns.map((column) => <option key={column.id} value={column.id}>{column.label}</option>)}
                  </select>
                </div>
                <p className="help">{t(isPhoneChannel ? locale.campaignFileRecipientHint : locale.campaignFileRecipientIdHint)}</p>
              </div>
              <h4 className={styles.sectionTitle}>{t(locale.campaignFileTemplateVariables)}</h4>
              {fieldsMapping.length > 0 && <p className="help mb-3">{t(locale.campaignHelpVariables)}</p>}
              {!template && <p className="help">{t(locale.campaignFileTemplateBeforeUpload)}</p>}
              {template && fieldsMapping.length === 0 && <p className="help">{t(locale.campaignAudienceNoVariables)}</p>}
              {fieldsMapping.map((mapping, index) => {
                const sampleValue = mapping.kind === "file_column" ? activeSample?.values[mapping.value] : undefined;
                return (
                  <div className={styles.mapping} key={`${mapping.source}-${index}`}>
                    <div className={styles.mappingTitle}><code>{mapping.source}</code></div>
                    <p className="help mb-3">{t(locale.campaignHelpToken, { token: mapping.source })}</p>
                    <div className={styles.mappingFields}>
                      <div>
                        <label className="label" htmlFor={`mapping-kind-${index}`}>{t(locale.campaignFileSource)}</label>
                        <div className="select is-fullwidth">
                          <select id={`mapping-kind-${index}`} value={mapping.kind} onChange={(event) => updateMapping(index, { kind: event.target.value as CampaignFileVariableMapping["kind"], value: "" })}>
                            <option value="file_column">{t(locale.campaignFileFileColumn)}</option>
                            <option value="contact_field">{t(locale.campaignFileContactField)}</option>
                            <option value="literal">{t(locale.campaignFileCustomText)}</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="label" htmlFor={`mapping-value-${index}`}>{t(mapping.kind === "file_column" ? locale.campaignFileColumn : mapping.kind === "contact_field" ? locale.campaignFileContactField : locale.campaignFileCustomText)}</label>
                        {mapping.kind === "literal" ? (
                          <input id={`mapping-value-${index}`} className="input" value={mapping.value} onChange={(event) => updateMapping(index, { value: event.target.value })} />
                        ) : (
                          <div className="select is-fullwidth">
                            <select id={`mapping-value-${index}`} value={mapping.value} onChange={(event) => updateMapping(index, { value: event.target.value })}>
                              <option value="">{t(locale.campaignFileChooseMapping)}</option>
                              {mapping.kind === "file_column"
                                ? preview.columns.map((column) => <option key={column.id} value={column.id}>{column.label}</option>)
                                : contactFields.map((field) => <option key={field} value={field}>{field}</option>)}
                            </select>
                          </div>
                        )}
                        {sampleValue != null && sampleValue !== "" && <p className="help">{t(locale.campaignAudienceExample, { value: String(sampleValue).slice(0, 80) })}</p>}
                      </div>
                    </div>
                    <p className="help">{t(mapping.kind === "file_column" ? locale.campaignHelpFileValue : mapping.kind === "contact_field" ? locale.campaignHelpContactValue : locale.campaignHelpLiteralValue)}</p>
                    <details className={styles.fallback}>
                      <summary>{t(locale.campaignFileFallback)}{mapping.fallback ? ` · ${mapping.fallback.slice(0, 40)}` : ""}</summary>
                      <p className="help mb-2">{t(locale.campaignHelpFallback)}</p>
                      <input id={`mapping-fallback-${index}`} aria-label={`${mapping.source}: ${t(locale.campaignFileFallback)}`} className="input" value={mapping.fallback || ""} onChange={(event) => updateMapping(index, { fallback: event.target.value || null })} />
                    </details>
                  </div>
                );
              })}
              {fieldsMapping.some((mapping) => mapping.kind === "contact_field") && <p className="help mt-3">{t(locale.campaignFileLiveContactHint)}</p>}
              <details className={styles.advanced}>
                <summary>{t(locale.campaignFileAdvancedSettings)}</summary>
                <div className={styles.advancedFields}>
                  <div>
                    <label className="label" htmlFor="campaign-header-row">{t(locale.campaignFileHeaderRow)}</label>
                    <input id="campaign-header-row" className="input" type="number" min={1} max={50001} value={configuration.headerRowNumber || preview.headerRowNumber} onChange={(event) => {
                      const value = Number(event.target.value);
                      updateConfiguration({ headerRowNumber: value > 0 ? value : null, recipientColumnId: null, firstNameColumnId: null, lastNameColumnId: null }, true);
                    }} />
                    <p className="help">{t(locale.campaignHelpHeader)}</p>
                  </div>
                  <div>
                    <h4 className="label">{t(locale.campaignFileNameSettings)}</h4>
                    <p className="help mb-3">{t(locale.campaignFileNameScope)}</p>
                    <div className={styles.mappingFields}>
                      {(["firstNameColumnId", "lastNameColumnId"] as const).map((key) => (
                        <div key={key}>
                          <label className="label" htmlFor={`campaign-${key}`}>{t(key === "firstNameColumnId" ? locale.firstName : locale.lastName)}</label>
                          <div className="select is-fullwidth">
                            <select id={`campaign-${key}`} value={configuration[key] || ""} onChange={(event) => updateConfiguration({ [key]: event.target.value || null })}>
                              <option value="">{t(locale.campaignFileNone)}</option>
                              {preview.columns.map((column) => <option key={column.id} value={column.id}>{column.label}</option>)}
                            </select>
                          </div>
                          <p className="help">{t(key === "firstNameColumnId" ? locale.campaignHelpFirstName : locale.campaignHelpLastName)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="label" htmlFor="campaign-invalid-policy">{t(locale.campaignFileRowIssues)}</label>
                    <div className="select is-fullwidth">
                      <select id="campaign-invalid-policy" value={configuration.invalidRowPolicy} onChange={(event) => updateConfiguration({ invalidRowPolicy: event.target.value as "block" | "skip" })}>
                        <option value="block">{t(locale.campaignFileBlockIssues)}</option>
                        <option value="skip">{t(locale.campaignFileSkipIssues)}</option>
                      </select>
                    </div>
                    <p className="help">{t(locale.campaignHelpInvalidRows)}</p>
                  </div>
                  <div>
                    <label className="label" htmlFor="campaign-duplicate-policy">{t(locale.campaignFileDuplicatePolicy)}</label>
                    <div className="select is-fullwidth">
                      <select id="campaign-duplicate-policy" value={configuration.duplicatePolicy} onChange={(event) => updateConfiguration({ duplicatePolicy: event.target.value as "keep_all" | "keep_first" })}>
                        <option value="keep_all">{t(locale.campaignFileKeepAll)}</option>
                        <option value="keep_first">{t(locale.campaignFileKeepFirst)}</option>
                      </select>
                    </div>
                    <p className="help">{t(locale.campaignHelpDuplicates)}</p>
                    {!!preview.stats.duplicateRows && <p className="help">{t(locale.campaignFileDuplicateExplanation, { count: preview.stats.duplicateRows })}</p>}
                  </div>
                </div>
              </details>
            </>
          )}
        </section>

        <aside className={`${styles.panel} ${styles.previewPanel}`}>
          <h3 className={styles.sectionTitle}>{t(locale.campaignFileMessagePreview)}</h3>
          <p className="help mb-3">{t(locale.campaignHelpPreview)}</p>
          {!template ? (
            <div className={styles.emptyState}><FiMessageSquare aria-hidden="true" /><p>{t(locale.campaignFileTemplateBeforeUpload)}</p></div>
          ) : (
            <>
              <div className={styles.previewHeader}>
                <span className={styles.previewAvatar}><FiUser aria-hidden="true" /></span>
                <div className={styles.previewMeta}>
                  <strong>{activeSample?.recipient || activeSample?.recipientSourceValue || template.name}</strong>
                  <small>{activeSample ? t(locale.campaignFilePreviewRow, { row: activeSample.rowNumber }) : t(locale.campaignAudiencePreviewHint)}</small>
                </div>
              </div>
              <div className={styles.messageCanvas} aria-live="polite" aria-busy={pending || Boolean(draft.previewStale)}>
                <div className={styles.messageBubble}>
                  {renderedSample}
                  {template.meta?.footer && <p className="help mt-3">{String(template.meta.footer)}</p>}
                </div>
                {mediaConfigured && <p className="help mt-3">{t(locale.campaignFileNoMediaPreview)}</p>}
                {templateButtons.map((button: any, index: number) => (
                  <button key={index} type="button" className="button is-small is-fullwidth mt-2" disabled>{String(button.text || button.title || button.label || "")}</button>
                ))}
              </div>
              {(pending || draft.previewStale) && <p className="help mt-2" role="status">{t(locale.campaignFileReading)}</p>}
              {preview && preview.samplePage.total > 0 && (
                <div className={styles.previewActions}>
                  <button type="button" className="button is-small" onClick={() => void sendPreview(Math.max(0, sampleOffset - 1))} disabled={pending || sampleOffset <= 0}>{t(locale.campaignFilePrevious)}</button>
                  <span>{t(locale.campaignFileSampleCount, { current: Math.min(sampleOffset + 1, preview.samplePage.total), total: preview.samplePage.total })}</span>
                  <button type="button" className="button is-small" onClick={() => void sendPreview(sampleOffset + 1)} disabled={pending || sampleOffset + 1 >= preview.samplePage.total}>{t(locale.campaignFileNext)}</button>
                </div>
              )}
              {activeSampleIssues.length > 0 && !pending && !draft.previewStale && (
                <ul className="help is-danger mt-3">{activeSampleIssues.map((issue: CampaignFileIssue, index: number) => {
                  const source = issue.message.match(/template variable\s+{{\s*([^{}]+?)\s*}}/i)?.[1]?.trim();
                  const mappingIndex = source ? fieldsMapping.findIndex((mapping) => mapping.source.replace(/[{}]/g, "").trim() === source) : -1;
                  return <li key={`${issue.code}-${index}`}>
                    {issueMessage(issue)}
                    {mappingIndex >= 0 && <button type="button" className="button is-text is-small" onClick={() => document.getElementById(`mapping-value-${mappingIndex}`)?.focus()}>{t(locale.campaignFileReviewMapping)}</button>}
                  </li>;
                })}</ul>
              )}
              <details className={styles.templateDetails}>
                <summary>{t(locale.campaignFileTemplate)} · {template.name}</summary>
                <p>{template.language || template.locale || ""} · {template.contentType || "text"}</p>
                <p>{message}</p>
              </details>
            </>
          )}
        </aside>
      </div>

      {preview && (
        <>
          {preview.stats.dataRows > 0 && !pending && !draft.previewStale && (
            <div className={`${styles.notice} ${preview.canLaunch ? styles.noticeSuccess : styles.noticeWarning}`} role="status">
              {preview.canLaunch ? <strong>{t(locale.campaignAudienceReady, { count: preview.stats.messagesReady })}</strong> : (
                <><strong>{t(locale.campaignAudienceFinishSetup)}</strong><ul>{preview.blockingReasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></>
              )}
              {preview.canLaunch && <p>{t(locale.campaignHelpReady)}</p>}
              {(configuration.invalidRowPolicy === "skip" || configuration.duplicatePolicy === "keep_first") && <p>{t(locale.campaignFileSkippedRows, { count: preview.stats.skippedRows })}</p>}
            </div>
          )}
          <details className={`${styles.panel} ${styles.dataDetails}`}>
            <summary>{t(locale.campaignFileDataAndIssues)} · {preview.stats.dataRows} {t(locale.campaignFileDataRows)}{preview.issueTotal > 0 ? ` · ${preview.issueTotal} ${t(locale.campaignAudienceIssues)}` : ""}</summary>
            <p className="help mt-3">{t(locale.campaignHelpData)}</p>
            <div className={styles.dataSummary}>
              <span>{preview.stats.validRecipientRows ?? "—"} {t(locale.campaignFileValidRecipients)}</span>
              <span>{preview.stats.missingRecipientRows ?? "—"} {t(locale.campaignFileMissingRecipients)}</span>
              <span>{preview.stats.invalidRecipientRows ?? "—"} {t(locale.campaignFileInvalidRecipients)}</span>
              <span>{preview.stats.uniqueRecipients ?? "—"} {t(locale.campaignFileUniqueRecipients)}</span>
              <span>{preview.stats.blankRowsIgnored} {t(locale.campaignFileBlankRows)}</span>
              {(preview.stats.identityConflictRecipients ?? 0) > 0 && <span>{t(locale.campaignFileIdentityCount, { count: preview.stats.identityConflictRecipients })}</span>}
            </div>
            {preview.hiddenRowsIncluded && <p className="help mt-2">{t(locale.campaignFileHiddenRows, { count: preview.hiddenRowCount })}</p>}
            {preview.issues.length > 0 && (
              <>
                {!pending && !draft.previewStale && preview.issues.some((issue) => issue.code === "identity_conflict") && <p className={`help mb-3 ${configuration.invalidRowPolicy === "block" ? "has-text-danger" : "has-text-warning-dark"}`}>
                  {t(configuration.invalidRowPolicy === "block" ? locale.campaignFileIssuesBlocked : locale.campaignFileIssuesSkipped)}
                </p>}
                <div className="table-container">
                  <table className="table is-fullwidth">
                    <thead><tr><th>{t(locale.campaignFileSheet)}</th><th>{t(locale.campaignFileRow)}</th><th>{t(locale.campaignFileColumn)}</th><th>{t(locale.campaignResultsRecipient)}</th><th>{t(locale.campaignFileReason)}</th></tr></thead>
                    <tbody>{preview.issues.map((issue, index) => <tr key={`${issue.rowNumber}-${issue.code}-${index}`}>
                      <td>{issue.sheet}</td><td>{issue.rowNumber}</td><td>{issue.column}</td>
                      <td><bdi>{issue.recipient || "—"}</bdi></td>
                      <td>
                        <p>{issueMessage(issue)}</p>
                        {(issue.conflictingContacts?.length ?? 0) > 0 && <ul className="mt-2" aria-label={t(locale.campaignFileConflictingChats)}>
                          {issue.conflictingContacts?.map((contact) => <li key={contact.id}>
                            <Link to={`${routeTo(routes.chats).replace(":chatId?", encodeURIComponent(contact.id))}?tab=all`} target="_blank" rel="noopener noreferrer" title={t(locale.campaignResultsOpenChat)}>
                              <bdi>{t(locale.campaignFileChatIdentity, { id: contact.id, number: contact.senderId })}</bdi><FiExternalLink className="ml-1" aria-hidden="true" />
                            </Link>
                          </li>)}
                        </ul>}
                      </td>
                    </tr>)}</tbody>
                  </table>
                </div>
                {preview.issueTotal > preview.issues.length && <button type="button" className="button is-small mt-2" disabled={loadingMoreIssues || pending} onClick={() => { setLoadingMoreIssues(true); void sendPreview(sampleOffset, preview.issues.length, true); }}>{t(locale.campaignFileLoadMoreIssues)}</button>}
              </>
            )}
            <p className="help mt-3">{t(locale.campaignFileSampleRowsShown, { count: preview.sampleRows.length, total: preview.samplePage.total })}</p>
            <div className="table-container">
              <table className="table is-fullwidth">
                <thead><tr><th>{t(locale.campaignFileRow)}</th>{preview.columns.map((column) => <th key={column.id}>{column.label}</th>)}</tr></thead>
                <tbody>{preview.sampleRows.map((row) => <tr key={row.rowNumber}><td>{row.rowNumber}</td>{preview.columns.map((column) => <td key={column.id}>{rowValue(row, column.id)}</td>)}</tr>)}</tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </div>
  );
}

export { initialConfiguration, blankMappings };
